import { assert, log } from '../../../app/logger.js';
import { AsapPromise } from '../../../util/asap_promise.js';
import { loadScript } from '../../../util/util.js';
import { window } from '../../../window.js';
import { MapBaseMixin } from '../base/map_base_mixin.js';
import { EventedGoogleMaps } from './evented_googlemaps.js';

/**
 * A wrapper around GoogleMaps map class. Loads Google Maps API.
 */
class MapGoogleMaps extends MapBaseMixin(EventedGoogleMaps) {
  static async create(el, options) {
    assert(window.kGoogleMapsKey, 'No Google Maps key');
    assert(window.kGoogleMapsVersion, 'No Google Maps version');

    if (!this.loadScriptPromise) {
      assert(
        !window.initGoogleMaps,
        `Global initGoogleMaps() function is already defined`
      );
      assert(
        !window.initGoogleMapsPromise,
        `Global initGoogleMapsPromise variable is already defined`
      );

      window.initGoogleMapsPromise = new AsapPromise();
      window.initGoogleMaps = () => window.initGoogleMapsPromise.resolve();

      this.loadScriptPromise = loadScript(
        `https://maps.googleapis.com/maps/api/js?v=${window.kGoogleMapsVersion}&key=${window.kGoogleMapsKey}&callback=initGoogleMaps&libraries=places`
      );
    }
    await this.loadScriptPromise.then(() => window.initGoogleMapsPromise);
    return new this(el, options);
  }

  get bounds() {
    let b = this.native.getBounds();
    return (
      b && {
        north: b.getNorthEast().lat(),
        east: b.getNorthEast().lng(),
        south: b.getSouthWest().lat(),
        west: b.getSouthWest().lng(),
      }
    );
  }

  fitBounds({ north, south, east, west }) {
    log(
      `[map:googlemaps] fitBounds (south: ${south}, east: ${east}, north: ${north}, west: ${west})`
    );
    assert(south, `GoogleMaps.fitBounds: no south`);
    assert(east, `GoogleMaps.fitBounds: no east`);
    assert(north, `GoogleMaps.fitBounds: no north`);
    assert(west, `GoogleMaps.fitBounds: no west`);
    this.native.fitBounds({ north, south, east, west });
  }

  panTo({ lat, lng, zoom }) {
    log(`[map:googlemaps] panTo (${lat}, ${lng}), zoom: ${zoom}`);
    this.native.panTo({ lat, lng });
    if (zoom) {
      this.zoom = zoom;
    }
  }

  panBy(x, y) {
    log(`[map:googlemaps] panBy (${x}, ${y})`);
    this.native.panBy(x, y);
  }

  get center() {
    return {
      lat: this.native.getCenter().lat(),
      lng: this.native.getCenter().lng(),
    };
  }

  get zoom() {
    return this.native.getZoom();
  }
  set zoom(v) {
    this.native.setZoom(v);
  }

  switchMap(type) {
    this.native.setMapTypeId(type);
  }

  drawGrid({ south, north, west, east, gap_lat, gap_lng }) {
    if (this.lines) {
      for (let line of this.lines) {
        line.setMap(null);
      }
    }

    const lat_cnt = Math.ceil((north - south) / gap_lat);
    const lng_cnt = Math.ceil((east - west) / gap_lng);

    this.lines = [];
    for (let i = 0; i < lat_cnt; i++) {
      for (let j = 0; j < lng_cnt; j++) {
        this.lines.push(
          new window.google.maps.Polyline({
            map: this.native,
            path: [
              { lat: south + i * gap_lat, lng: west },
              { lat: south + i * gap_lat, lng: east },
            ],
            strokeColor: 'black',
            strokeWeight: 1,
          })
        );

        this.lines.push(
          new window.google.maps.Polyline({
            map: this.native,
            path: [
              { lat: south, lng: west + j * gap_lng },
              { lat: north, lng: west + j * gap_lng },
            ],
            strokeColor: 'black',
            strokeWeight: 1,
          })
        );
      }
    }
  }

  // Protected

  createNative() {
    return new window.google.maps.Map(this.el, this.nativeOptions);
  }

  static getDefaultNativeOptions() {
    return {
      clickableIcons: true,
      disableDefaultUI: false,
      mapTypeControl: false,
      fullscreenControl: false,
      scaleControl: true,
      streetViewControl: false,
      zoomControl: false,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'poi.park',
          stylers: [{ visibility: 'on' }],
        },
        {
          featureType: 'water',
          elementType: 'labels',
          stylers: [{ visibility: 'on' }],
        },
      ],
    };
  }

  static get nativeOptionsMapper() {
    return Object.assign({}, super.nativeOptionsMapper, {
      center: 'center',
      zoom: 'zoom',
    });
  }

  static loadScriptPromise = null;
}

export { MapGoogleMaps };
